import { clearUnifiedCaptureRetries } from '@/lib/incidents/unified-capture';

/**
 * Pure storage helpers for useFormDraft, extracted so the load/persist contract
 * can be unit-tested without a DOM (mirrors hooks/merge-persisted-preference.ts).
 */

export interface StoredDraft<T> {
  value: T;
  savedAt: number;
}

/** Prefix of every identity-scoped form draft key. */
export const FORM_DRAFT_PREFIX = 'statura_draft:';
/** Pre-12-Oct-2026 drafts were keyed by form only, so any operator in the tab could restore them. */
const LEGACY_FORM_DRAFT_PREFIX = 'statura_draft_';

/**
 * The signed-in identity a draft belongs to: tenant and user ids only, never names
 * or other personal details. Null until both are known, which disables drafts.
 */
export function draftScope(
  identity: { id?: string | null; tenantId?: string | null } | null | undefined,
): string | null {
  return identity?.id && identity.tenantId ? `${identity.tenantId}.${identity.id}` : null;
}

/** sessionStorage key for a draft. `version` invalidates older incompatible shapes. */
export function draftStorageKey(key: string, version: number | undefined, scope: string): string {
  return `${FORM_DRAFT_PREFIX}${scope}:${key}${version ? `_v${version}` : ''}`;
}

function draftKeys(storage: Storage): string[] {
  return Array.from({ length: storage.length }, (_, i) => storage.key(i)).filter(
    (key): key is string =>
      !!key && (key.startsWith(FORM_DRAFT_PREFIX) || key.startsWith(LEGACY_FORM_DRAFT_PREFIX)),
  );
}

/**
 * Drop every draft that is not the given identity's, including legacy unscoped
 * drafts, so another operator's PHI is never restored in a retained tab.
 */
export function pruneForeignFormDrafts(storage: Storage, scope: string): void {
  const own = `${FORM_DRAFT_PREFIX}${scope}:`;
  for (const key of draftKeys(storage)) if (!key.startsWith(own)) storage.removeItem(key);
}

/** Drop every form draft in this tab (explicit sign-out). */
export function clearAllFormDrafts(storage: Storage): void {
  for (const key of draftKeys(storage)) storage.removeItem(key);
}

/** Normal sign-out is a native POST: clear before navigation, including from other pages. */
export function installFormDraftSignOutCleanup(): () => void {
  const onSubmit = (event: Event) => {
    const form = event.target;
    if (
      form instanceof HTMLFormElement &&
      new URL(form.action, window.location.href).pathname === '/auth/signout'
    ) {
      try {
        clearAllFormDrafts(window.sessionStorage);
        clearUnifiedCaptureRetries(window.sessionStorage);
      } catch {
        /* Storage denial must not prevent sign-out. */
      }
    }
  };
  document.addEventListener('submit', onSubmit, true);
  return () => document.removeEventListener('submit', onSubmit, true);
}

/** JSON.stringify that returns null instead of throwing on circular/unserializable input. */
export function safeStringify(value: unknown): string | null {
  try {
    return JSON.stringify(value);
  } catch {
    return null;
  }
}

/** Parse a stored draft blob, tolerating corrupt/legacy values (returns null). */
export function parseStoredDraft<T>(raw: string | null): StoredDraft<T> | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && 'value' in parsed) {
      return parsed as StoredDraft<T>;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Whether the current (serialized) form value should be persisted as a draft.
 * Persist only once the value differs from the recorded initial baseline, so the
 * initial empty render never clobbers a restorable draft found on mount.
 * `baseline === null` means the baseline hasn't been recorded yet (initial render).
 */
export function shouldPersistDraft(
  serialized: string | null,
  baseline: string | null,
  restorePending = false,
): boolean {
  if (restorePending) return false;
  if (serialized === null) return false; // unserializable
  if (baseline === null) return false; // baseline not yet recorded
  return serialized !== baseline;
}
